'use client';

import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { ResponsiveImage } from '../responsiveimage';
import { Button } from '../button';


interface NavigationItem {
  name: string;
  href: string;
  isBooking?: boolean;
}

interface NavProps {
  logo: {
    light: string;
    dark: string;
    alt: string;
  };
  desktopItems: NavigationItem[];
  mobileItems: NavigationItem[];
  darkTextPages?: string[];
}

export function Navigation({ logo, desktopItems, mobileItems, darkTextPages = [] }: NavProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboveThreshold, setIsAboveThreshold] = useState(true);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const pathname = usePathname();

  // const textColor = isScrolled 
  //   ? 'text-white' 
  //   : darkTextPages.some(page => pathname.startsWith(page))
  //     ? 'text-primary-950'
  //     : 'text-white';

  const bgColor = isScrolled 
    ? 'bg-primary-950/90'  // When scrolled, always use semi-transparent dark background
    : darkTextPages.some(page => pathname.startsWith(page))
      ? 'bg-primary-950/90'   // On darkTextPages when not scrolled, use solid dark background
      : 'bg-transparent';   // On other pages when not scrolled, keep transparent

  // Determine which logo to show
  const logoSrc = isScrolled 
    ? logo.dark 
    : darkTextPages.some(page => pathname.startsWith(page))
      ? logo.dark
      : logo.dark;

  useEffect(() => {
    const threshold = window.innerHeight * 0.55;

    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      if (scrollPosition > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      if (scrollPosition > threshold) {
        setIsAboveThreshold(true);
      } else {
        setIsAboveThreshold(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled, isAboveThreshold]);

  return (
    <>
      <header 
        className={`w-full z-50 transition-all duration-500 ease-in-out border-x-8 md:border-x-[16px] border-t-8 md:border-t-[16px] border-white ${
          isScrolled
            ? isAboveThreshold
              ? `fixed top-0 left-0 right-0 bg-primary-950/90` // Both true
              : 'absolute top-[-100px] left-0 right-0' // Scrolled but not above threshold
            : isAboveThreshold
              ? `absolute top-0 left-0 right-0 ${bgColor} border-b border-b-white/50` // Not scrolled but above threshold
              : `absolute top-0 left-0 right-0 ${bgColor} border-b border-b-white/50` // Neither scrolled nor above threshold
        }`}
        role="banner"
      >
        <div className={`px-4 lg:px-6 xl:px-12 flex items-center justify-between transition-all duration-500 ease-in-out ${
          isScrolled ? 'py-3 mt-0' : 'py-4 mt-0'
        }`}>
          {/* Logo */}
          <a 
            href="/" 
            className="relative z-10 w-[121px]"
            aria-label="Under SXM - Return to homepage"
          >
            <ResponsiveImage
              src={logoSrc}
              alt={logo.alt}
              className="w-85 h-auto"
              height={37}
              loading="eager"
              priority
              width={121}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="flex-1 flex items-center justify-end">
            <nav 
              className="hidden lg:flex absolute left-1/2 -translate-x-1/2 lg:w-[600px] xl:w-[700px]"
              role="navigation"
              aria-label="Main navigation"
            >
              <ul className="flex items-center justify-between w-full">
                {desktopItems.map((item) => (
                  <li key={item.name} className="mx-0">
                    <a
                      href={item.href}
                      className='px-2 lg:px-4 text-center block font-display uppercase tracking-widest text-xs hover:text-highlight-500 transition-colors duration-300 text-white hover:cursor-pointer'
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>

              {/* Book Button */}
            <Button
              href="#"
              isBooking={true}
              className="hidden lg:inline-block px-2 lg:px-4 w-auto min-w-[80px] text-center font-display uppercase tracking-widest text-xs hover:text-highlight-500 hover:bg-transparent transition-colors duration-300 text-white hover:cursor-pointer border-0 focus:border-0 focus:outline-none focus:ring-0"
            >
              Book
            </Button>
            
            </nav>

            {/* Mobile Menu Button */}
            <button
              type="button"
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
              className="block relative z-10 hover:text-highlight-500 transition-colors duration-300 text-white" 
              aria-label={isMobileNavOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileNavOpen}
              aria-controls="mobile-navigation"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                className="rotate-[-40deg] inline-block" 
                aria-hidden="true"
              >
                <line 
                  x1="6" 
                  y1="7" 
                  x2="16" 
                  y2="7" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                />
                <line 
                  x1="4" 
                  y1="12" 
                  x2="20" 
                  y2="12" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                />
                <line 
                  x1="8" 
                  y1="17" 
                  x2="18" 
                  y2="17" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                />
              </svg>
              <span className="uppercase text-xs tracking-widest inline-block ml-2">Menu</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {isMobileNavOpen && (
        <div 
          className="fixed inset-0 bg-primary-950 bg-opacity-60 z-50"
          onClick={() => setIsMobileNavOpen(false)}
          role="presentation"
        />
      )}

      {/* Mobile Navigation Menu */}
      <div 
        id="mobile-navigation"
        className={`fixed top-0 right-0 h-full w-[260px] bg-primary-950 z-50 transform transition-transform duration-300 ease-in-out ${
          isMobileNavOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Mobile navigation menu"
      >
        <div className="h-full flex flex-col pt-12 px-8">
          {/* Close Button */}
          <button
            type="button"
            onClick={() => setIsMobileNavOpen(false)}
            className="absolute top-8 right-8 text-highlight-500 hover:text-white transition-colors duration-300"
            aria-label="Close navigation menu"
          >
            <X className="w-6 h-6" aria-hidden="true" />
          </button>
          <nav 
            role="navigation" 
            aria-label="Mobile navigation"
          >
            <ul className="text-right mt-10">
              {mobileItems.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.isBooking ? undefined : item.href}
                    className={`py-3 block font-display uppercase tracking-widest text-white text-xs hover:text-highlight-500 transition-colors duration-300`}
                    onClick={(e) => {
                      if (!item.isBooking) {
                        setIsMobileNavOpen(false);
                      }
                    }}
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          
          {/* Add Book Button */}
          <div className="mt-8 text-right">
            <Button
              variant="whitePrimary"
              isBooking={true}
            >
              Book Now
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// Re-export for backward compatibility
export { Navigation as Nav }; 